import React, { useMemo } from 'react';
import { Alert, Box, Button, Chip, Typography } from '@mui/material';
import { CTableWrapper } from '../../../dum';
import { formatMoney } from './expenses-summary-delta';
import {
    findFirstTimers,
    findMissing,
    NormalizedRow,
    PresenceRow,
    RECENT_WINDOW,
    atEntityLevel,
    atResourceLevel,
} from './expenses-summary-model';

// A first-timer row tagged with where it came from, so suppliers and resources
// can share one table instead of two half-empty ones.
type FirstTimerRow = PresenceRow & { kind: string };

function MissingTable({
    rows,
    nameColumnTitle,
    amountColumnTitle,
}: {
    rows: PresenceRow[];
    nameColumnTitle: string;
    amountColumnTitle: string;
}) {
    return (
        <CTableWrapper
            stickyHeader={false}
            items={[
                {
                    title: nameColumnTitle,
                    content: (row) => row.name,
                    cellGrow: 2,
                },
                {
                    title: amountColumnTitle,
                    textAlign: 'right',
                    content: (row) => formatMoney(row.amount),
                },
                {
                    title: 'Meses con gasto',
                    textAlign: 'right',
                    content: (row) => (
                        <Chip
                            size={'small'}
                            variant={'outlined'}
                            label={`${row.recentMonths ?? 0} de ${RECENT_WINDOW}`}
                            // A supplier present in every recent month is the
                            // strongest signal that this month is an oversight.
                            color={
                                (row.recentMonths ?? 0) >= RECENT_WINDOW
                                    ? 'error'
                                    : (row.recentMonths ?? 0) >= 3
                                    ? 'warning'
                                    : 'default'
                            }
                        />
                    ),
                },
            ]}
            data={rows}
        />
    );
}

export function ExpensesSummaryMissingPanel({
    accountRows,
    categoryRows,
    recurringAccountIds,
    year,
    // 1-indexed (backend convention).
    month,
    monthLabel,
    previousMonthLabel,
    onGenerateRecurring,
    includeCanceled,
}: {
    accountRows: NormalizedRow[];
    categoryRows: NormalizedRow[];
    recurringAccountIds: Set<number>;
    year: number;
    month: number;
    monthLabel: string;
    previousMonthLabel: string;
    onGenerateRecurring: () => void;
    includeCanceled: boolean;
}) {
    const { recurringMissing, otherMissing, firstTimers } = useMemo(() => {
        // Id 0 is the page's placeholder for a null account or resource ("Sin
        // proveedor" / "Sin recurso"). It is not an entity anyone can chase up,
        // so it never belongs in an actionable list.
        const isRealEntity = (row: PresenceRow) => row.id !== 0;

        const supplierPoints = atEntityLevel(accountRows);
        const missing = findMissing(supplierPoints, year, month).filter(
            isRealEntity,
        );

        // Detectors (a) and (b) from the plan are the same computation split by
        // the account's recurring flag: flagged suppliers are expected every
        // month, so their absence is an alert; the rest is a softer signal.
        const recurring: PresenceRow[] = [];
        const other: PresenceRow[] = [];
        missing.forEach((row) => {
            if (recurringAccountIds.has(row.id)) {
                recurring.push(row);
            } else {
                other.push(row);
            }
        });

        const newSuppliers: FirstTimerRow[] = findFirstTimers(
            supplierPoints,
            year,
            month,
        )
            .filter(isRealEntity)
            .map((row) => ({ ...row, kind: 'Proveedor' }));

        const newResources: FirstTimerRow[] = findFirstTimers(
            atResourceLevel(categoryRows),
            year,
            month,
        )
            .filter(isRealEntity)
            .map((row) => ({ ...row, kind: 'Recurso' }));

        return {
            recurringMissing: recurring,
            otherMissing: other,
            firstTimers: [...newSuppliers, ...newResources].sort(
                (a, b) => b.amount - a.amount,
            ),
        };
    }, [accountRows, categoryRows, recurringAccountIds, year, month]);

    const nothingFound =
        recurringMissing.length === 0 &&
        otherMissing.length === 0 &&
        firstTimers.length === 0;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* The whole panel compares the selected month against the previous
                one and nothing else — say so, because the tables above do use
                longer windows and the difference matters when reading this. */}
            <Typography variant={'caption'} sx={{ color: 'text.secondary' }}>
                Compara {monthLabel} contra {previousMonthLabel}.
                {includeCanceled
                    ? ' Con "Canceladas" activado, un gasto cancelado cuenta como registrado, así que la detección de faltantes queda incompleta.'
                    : ' Un gasto cancelado no cuenta como registrado: si no se reemplazó, aparece aquí.'}
            </Typography>

            {nothingFound ? (
                <Alert severity={'success'}>
                    No se detectaron faltantes ni movimientos nuevos en{' '}
                    {monthLabel}.
                </Alert>
            ) : null}

            {recurringMissing.length > 0 ? (
                <Box>
                    <Alert
                        severity={'error'}
                        sx={{ mb: 1 }}
                        action={
                            <Button
                                color={'inherit'}
                                size={'small'}
                                onClick={onGenerateRecurring}
                            >
                                Generar
                            </Button>
                        }
                    >
                        {recurringMissing.length} proveedor
                        {recurringMissing.length === 1 ? '' : 'es'} marcado
                        {recurringMissing.length === 1 ? '' : 's'} como
                        recurrente sin gasto registrado en {monthLabel}.
                    </Alert>
                    <MissingTable
                        rows={recurringMissing}
                        nameColumnTitle={'Proveedor recurrente'}
                        amountColumnTitle={previousMonthLabel}
                    />
                </Box>
            ) : null}

            {otherMissing.length > 0 ? (
                <Box>
                    <Alert severity={'warning'} sx={{ mb: 1 }}>
                        {otherMissing.length} proveedor
                        {otherMissing.length === 1 ? '' : 'es'} con gasto en{' '}
                        {previousMonthLabel} y ninguno en {monthLabel}.
                    </Alert>
                    <MissingTable
                        rows={otherMissing}
                        nameColumnTitle={'Proveedor'}
                        amountColumnTitle={previousMonthLabel}
                    />
                </Box>
            ) : null}

            {firstTimers.length > 0 ? (
                <Box>
                    <Alert severity={'info'} sx={{ mb: 1 }}>
                        {firstTimers.length} movimiento
                        {firstTimers.length === 1 ? '' : 's'} sin antecedentes
                        en los 12 meses previos — revisa que no sea un
                        proveedor duplicado o una captura mal clasificada.
                    </Alert>
                    <CTableWrapper
                        stickyHeader={false}
                        items={[
                            {
                                title: 'Tipo',
                                content: (row) => row.kind,
                            },
                            {
                                title: 'Nombre',
                                content: (row) => row.name,
                                cellGrow: 2,
                            },
                            {
                                title: monthLabel,
                                textAlign: 'right',
                                content: (row) => formatMoney(row.amount),
                            },
                        ]}
                        data={firstTimers}
                    />
                </Box>
            ) : null}
        </Box>
    );
}
